import clsx from "clsx";
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "tertiary";
type Size = "default" | "large";

interface Common {
  variant?: Variant;
  size?: Size;
  /** Surface the secondary variant sits on; its fill is set explicitly, never transparent (02_colors.md). */
  surface?: "canvas" | "card" | "card-tinted";
  children: ReactNode;
  className?: string;
  /** Native on a button; on a link it removes the href, marks aria-disabled, and drops it from the tab order. */
  disabled?: boolean;
}
type ButtonProps = Common & ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };
type LinkProps = Common & AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

/**
 * Button per 10_component-anatomy.md: 40 px default / 48 px large, radius-sm,
 * Inter 500, hover colour only, a one-pixel downshift when pressed, and native
 * disabled semantics. Renders an <a> when given an href. data-variant names the
 * variant for styles-v2.css, which draws the secondary edge in --accent-outline
 * (Colour System v3.0.0: Periwinkle on dark, Ink cobalt on light). Tertiary is the
 * text button: no fill, no border, a brand-text label, underlined (design-system
 * Button.md), keeping the 40 / 48 px target. There is no inverse variant: the
 * system has one filled control colour on every ground (10_component-anatomy.md
 * section 1), and a ground is forced with data-theme, not an inverse surface.
 */
export function Button(props: ButtonProps | LinkProps) {
  const { variant = "primary", size = "default", surface = "canvas", className, children, disabled, ...rest } = props;
  const classes = clsx(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm border font-medium leading-[1.3]",
    variant !== "tertiary" && "no-underline",
    "focus-visible:outline-2 focus-visible:outline-offset-2 active:translate-y-px",
    "disabled:bg-disabled disabled:text-on-disabled disabled:border-transparent disabled:cursor-not-allowed disabled:translate-y-0",
    disabled && "bg-disabled! text-on-disabled! border-transparent! cursor-not-allowed pointer-events-none",
    size === "large" ? "min-h-12 text-body" : "min-h-10 text-body",
    // A text button lines up with the text around it, so it takes no side padding.
    variant !== "tertiary" && (size === "large" ? "px-6" : "px-4"),
    variant === "primary" && "bg-action text-on-action border-transparent hover:bg-action-hover hover:text-on-action focus-visible:outline-focus",
    variant === "secondary" && [
      "border-rule-strong text-anchor hover:bg-card-tinted hover:text-anchor focus-visible:outline-focus",
      surface === "canvas" && "bg-canvas",
      surface === "card" && "bg-card",
      surface === "card-tinted" && "bg-card-tinted",
    ],
    variant === "tertiary" && "border-transparent bg-transparent text-text-brand underline underline-offset-4 hover:decoration-2 focus-visible:outline-focus",
    className,
  );
  if ("href" in props && props.href) {
    const { href, ...a } = rest as AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };
    return (
      <a href={disabled ? undefined : href} aria-disabled={disabled || undefined} tabIndex={disabled ? -1 : undefined} data-variant={variant} className={classes} {...a}>
        {children}
      </a>
    );
  }
  const b = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button type={b.type ?? "button"} data-variant={variant} className={classes} disabled={disabled} {...b}>
      {children}
    </button>
  );
}
